import React, { useState, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Sparkles, Wand2, Loader2, AlertTriangle, CheckCircle2, XCircle, Tags, FileUp, FileText, FileSpreadsheet, X, Coins, HelpCircle, Columns3, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

const ACCEPTED = ".csv,.xlsx,.xls,.pdf,.xml";
const MAX_BYTES = 15 * 1024 * 1024; // 15 MB — must match backend cap

function formatApiError(detail, fallback = "Something went wrong.") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail?.msg || JSON.stringify(detail);
}

const ROW_STATUS_META = {
  Valid: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Valid" },
  Review_Price: { cls: "bg-sg-gold/15 text-sg-gold-deep border-sg-gold/50", icon: Coins, label: "Review Price" },
  Flagged: { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: AlertTriangle, label: "Flagged" },
  Rejected: { cls: "bg-red-50 text-red-800 border-red-200", icon: XCircle, label: "Rejected" },
};

function StatusPill({ status }) {
  const meta = ROW_STATUS_META[status] || ROW_STATUS_META.Rejected;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function flagBadge(f) {
  const cls = f.startsWith("pii_detected")
    ? "bg-red-50 text-red-800 border-red-200"
    : f.startsWith("missing_or_invalid_mahacriti")
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : f === "price_recovery_needed"
    ? "bg-amber-50 text-amber-800 border-amber-200"
    : f === "price_estimated_from_master"
    ? "bg-sg-gold/15 text-sg-gold-deep border-sg-gold/40"
    : "bg-gray-50 text-gray-700 border-gray-200";
  return <span key={f} className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}>{f.replace(/_/g, " ")}</span>;
}

function fileIcon(name) {
  const lower = (name || "").toLowerCase();
  if (lower.endsWith(".pdf")) return <FileText className="w-4 h-4" />;
  if (lower.endsWith(".csv") || lower.endsWith(".xlsx") || lower.endsWith(".xls")) return <FileSpreadsheet className="w-4 h-4" />;
  return <FileUp className="w-4 h-4" />;
}

export default function MagicDropPanel() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | uploading | preview | committing
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({});
  const [priceEdits, setPriceEdits] = useState({}); // idx -> string (user-entered price in lakhs)
  const [consent, setConsent] = useState(false);
  const [open, setOpen] = useState(false);
  // Column-mapping overrides: { header_name: field_key | "" (ignore) | "auto" }
  const [overrides, setOverrides] = useState({});
  const [remapLoading, setRemapLoading] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);

  // A row is "committable" when we can send a numeric price + it has bhk + locality.
  // - Valid rows: committable (price came from file, bhk/locality present)
  // - Review_Price: committable IF the master-estimated price is retained OR the agent edits it to a valid number
  // - Flagged (price_recovery_needed): committable only after agent supplies a price
  // - Rejected: never committable (bhk / locality still missing)
  const effectivePriceLakhs = (item, idx) => {
    const raw = priceEdits[idx];
    if (raw !== undefined && raw !== "") {
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    const fromMap = item?.mapped?.price_lakhs;
    return typeof fromMap === "number" ? fromMap : (fromMap ? Number(fromMap) : null);
  };
  const isCommittable = (item, idx) => {
    if (!item) return false;
    if (item.row_status === "Rejected") return false;
    const m = item.mapped || {};
    if (!m.bhk || !m.locality) return false;
    return effectivePriceLakhs(item, idx) !== null;
  };

  const validateAndSet = (f) => {
    if (!f) return;
    const lower = (f.name || "").toLowerCase();
    const okExt = [".csv", ".xlsx", ".xls", ".pdf", ".xml"].some((e) => lower.endsWith(e));
    if (!okExt) { toast.error("Unsupported file type. Upload CSV, XLSX, XLS, PDF or XML."); return; }
    if (f.size > MAX_BYTES) { toast.error("File too large (max 15 MB)."); return; }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    validateAndSet(f);
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    validateAndSet(f);
  };

  const onSync = async () => {
    if (!file) return;
    let toastId;
    try {
      setStage("uploading");
      toastId = toast.loading(`Uploading ${file.name}…`);
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/listings/magic-drop/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      applyPreviewData(data, { resetOverrides: true });
      setOpen(true);
      setStage("preview");
      const recovered = data.price_recovery_stats?.recovered || 0;
      const notFound = data.price_recovery_stats?.not_found || 0;
      const unmappedCount = (data.raw_headers || []).filter((h) => !h.detected_field).length;
      toast.success(
        `${data.valid_count} valid · ${data.review_price_count || 0} review price · ${data.flagged_count} flagged · ${data.rejected_count} rejected`
        + (recovered ? ` · ${recovered} price(s) recovered from Master Projects` : "")
        + (notFound ? ` · ${notFound} need manual entry` : "")
        + (unmappedCount ? ` · ${unmappedCount} unmapped column(s) — review mapping` : "")
        + (data.parsing_mode === "llm" ? " · AI-assisted" : ""),
        { id: toastId },
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Magic Drop failed."), { id: toastId });
      setStage("idle");
    }
  };

  // Shared helper — refreshes preview state after upload OR remap.
  const applyPreviewData = (data, { resetOverrides = false } = {}) => {
    setPreview(data);
    const nextSel = {};
    data.items.forEach((it, idx) => {
      nextSel[idx] = it.row_status === "Valid" || it.row_status === "Review_Price";
    });
    setSelected(nextSel);
    setPriceEdits({});
    if (resetOverrides) {
      const initial = {};
      (data.raw_headers || []).forEach((h) => {
        // "auto" = follow default heuristic (whatever the backend detected).
        initial[h.name] = "auto";
      });
      setOverrides(initial);
      // Auto-open the mapping panel when unmapped columns exist so agents see it.
      const anyUnmapped = (data.raw_headers || []).some((h) => !h.detected_field);
      setMappingOpen(anyUnmapped);
    }
  };

  const onRemap = async () => {
    if (!preview) return;
    if (!preview.raw_rows || !preview.raw_rows.length) {
      toast.error("Remapping is only available for CSV / Excel / PDF-table uploads.");
      return;
    }
    setRemapLoading(true);
    try {
      const { data } = await api.post("/listings/magic-drop/remap", {
        filename: preview.filename || file?.name || "upload",
        raw_rows: preview.raw_rows,
        overrides,
      });
      applyPreviewData(data, { resetOverrides: false });
      toast.success(
        `Re-mapped · ${data.valid_count} valid · ${data.review_price_count || 0} review price · ${data.flagged_count} flagged · ${data.rejected_count} rejected`,
      );
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Remap failed."));
    } finally {
      setRemapLoading(false);
    }
  };

  const onCommit = async () => {
    if (!preview) return;
    if (!consent) {
      toast.error("Please tick the data-ownership / RERA-compliance checkbox.");
      return;
    }
    // Build the payload — merge any inline price edits into each selected item.
    const items = preview.items
      .map((it, idx) => ({ it, idx }))
      .filter(({ it, idx }) => selected[idx] && isCommittable(it, idx))
      .map(({ it, idx }) => {
        const priceOverride = priceEdits[idx];
        const editedNumber = priceOverride !== undefined && priceOverride !== "" ? Number(priceOverride) : null;
        const finalPrice = editedNumber && Number.isFinite(editedNumber) && editedNumber > 0
          ? editedNumber
          : (it.mapped?.price_lakhs ?? null);
        return {
          ...it,
          mapped: {
            ...it.mapped,
            price_lakhs: finalPrice,
            // If the agent overrode the master-recovered price, mark it as manual.
            price_source: editedNumber ? "manual" : (it.mapped?.price_source || "file"),
          },
        };
      });
    if (!items.length) {
      toast.error("Select at least one committable row (Valid or Review-Price with a filled price).");
      return;
    }
    setStage("committing");
    try {
      const { data } = await api.post("/listings/magic-drop/commit", {
        feed_url: preview.feed_url,
        items,
        consent_confirmed: true,
      });
      toast.success(`Imported ${data.inserted_count} listings as Draft. Open Listings to review & publish.`);
      setOpen(false);
      setPreview(null);
      setFile(null);
      setConsent(false);
      setPriceEdits({});
      setOverrides({});
      setMappingOpen(false);
      setStage("idle");
      navigate("/dashboard/listings");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail, "Commit failed."));
      setStage("preview");
    }
  };

  const committableCount = preview
    ? preview.items.filter((it, idx) => isCommittable(it, idx)).length
    : 0;
  const allChecked = preview && committableCount > 0 && preview.items.every((it, idx) => !isCommittable(it, idx) || selected[idx]);
  const toggleAll = () => {
    if (!preview) return;
    const next = {};
    preview.items.forEach((it, i) => { next[i] = !allChecked && isCommittable(it, i); });
    setSelected(next);
  };

  // Column-mapping derived state — used to size CTAs + banners.
  const hasRawHeaders = !!(preview && preview.raw_headers && preview.raw_headers.length);
  const mappableFields = (preview && preview.mappable_fields) || [
    "price", "bhk", "carpet_area_sqft", "locality", "property_type", "description",
    "rera", "layout_notes", "unit_variant", "project_name", "developer",
  ];
  const overrideMetrics = useMemo(() => {
    if (!hasRawHeaders) return { unmappedCount: 0, changedCount: 0, ignoredCount: 0 };
    let unmapped = 0;
    let changed = 0;
    let ignored = 0;
    for (const h of preview.raw_headers) {
      const choice = overrides[h.name];
      const detected = h.detected_field || null;
      if (choice === "") ignored++;
      else if (choice && choice !== "auto" && choice !== detected) changed++;
      if (!detected && (choice === "auto" || choice === undefined)) unmapped++;
    }
    return { unmappedCount: unmapped, changedCount: changed, ignoredCount: ignored };
  }, [hasRawHeaders, preview, overrides]);
  const canRemap = hasRawHeaders && (overrideMetrics.changedCount > 0 || overrideMetrics.ignoredCount > 0);

  const uploading = stage === "uploading";

  return (
    <div className="bg-white border border-sg-line rounded-3xl p-7 mt-12" data-testid="magic-drop-panel">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-sg-cream-soft border border-sg-line flex items-center justify-center text-sg-gold-deep">
          <Wand2 className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="overline text-sg-gold-deep">MAGIC DROP</div>
          <h3 className="font-display text-2xl text-sg-navy leading-tight">
            Drop a file. Get <span className="font-serif-italic text-sg-gold-deep">listings.</span>
          </h3>
          <p className="text-sm text-sg-mute mt-1 max-w-xl">
            Upload your CSV, Excel, or PDF brochure. We&apos;ll auto-map fields (price, BHK, locality, RERA), flag PII & compliance issues, and let you pick which rows to import — all as Draft.
          </p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`mt-5 cursor-pointer rounded-2xl border-2 border-dashed transition-colors px-6 py-8 text-center ${dragging ? "border-sg-gold-deep bg-sg-cream-soft" : "border-sg-line bg-sg-cream-soft/40 hover:bg-sg-cream-soft"}`}
        data-testid="magic-drop-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED}
          onChange={onPick}
          className="hidden"
          data-testid="magic-drop-file-input"
        />
        {!file ? (
          <div className="flex flex-col items-center gap-2 text-sg-mute">
            <FileUp className="w-7 h-7 text-sg-gold-deep" />
            <div className="text-sm">
              <span className="font-semibold text-sg-navy">Click to upload</span> or drag &amp; drop
            </div>
            <div className="text-xs">CSV · XLSX · XLS · PDF · XML &middot; up to 15 MB</div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-3 text-sg-navy" data-testid="magic-drop-selected-file">
            <span className="text-sg-gold-deep">{fileIcon(file.name)}</span>
            <span className="font-mono text-sm truncate max-w-[260px]">{file.name}</span>
            <span className="text-xs text-sg-mute">{(file.size / 1024).toFixed(1)} KB</span>
            <button
              onClick={(e) => { e.stopPropagation(); setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
              className="text-sg-mute hover:text-red-700"
              data-testid="magic-drop-clear-file"
              type="button"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={onSync}
          disabled={uploading || !file}
          className="h-12 rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white px-6 sm:min-w-[200px]"
          data-testid="magic-drop-sync"
        >
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          {uploading ? "Extracting…" : "Extract Listings"}
        </Button>
      </div>
      <p className="mt-3 text-xs text-sg-mute">
        Auto-detects headers like <span className="font-mono">Price</span>, <span className="font-mono">BHK</span>, <span className="font-mono">Locality</span>, <span className="font-mono">Carpet Area</span>, <span className="font-mono">RERA</span>. PDFs without tables fall back to AI extraction.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="magic-drop-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-sg-navy">Mapping preview</DialogTitle>
            <DialogDescription>
              Review the auto-mapped fields. Flagged rows still import — but review the flag before you publish.
            </DialogDescription>
          </DialogHeader>

          {preview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
                <div className="bg-sg-cream-soft border border-sg-line rounded-xl p-3" data-testid="mdp-stat-total">
                  <div className="text-xs text-sg-mute">FOUND</div>
                  <div className="font-display text-2xl text-sg-navy">{preview.total_found}</div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="mdp-stat-valid">
                  <div className="text-xs text-emerald-800">VALID</div>
                  <div className="font-display text-2xl text-emerald-900">{preview.valid_count}</div>
                </div>
                <div className="bg-sg-gold/15 border border-sg-gold/40 rounded-xl p-3" data-testid="mdp-stat-review-price">
                  <div className="text-xs text-sg-gold-deep">REVIEW PRICE</div>
                  <div className="font-display text-2xl text-sg-navy">{preview.review_price_count || 0}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="mdp-stat-flagged">
                  <div className="text-xs text-amber-800">FLAGGED</div>
                  <div className="font-display text-2xl text-amber-900">{preview.flagged_count}</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-3" data-testid="mdp-stat-rejected">
                  <div className="text-xs text-red-800">REJECTED</div>
                  <div className="font-display text-2xl text-red-900">{preview.rejected_count}</div>
                </div>
              </div>

              {preview.price_recovery_stats && (preview.price_recovery_stats.recovered > 0 || preview.price_recovery_stats.not_found > 0) && (
                <div className="bg-sg-cream-soft border border-sg-gold/40 rounded-xl p-3 text-xs text-sg-navy" data-testid="mdp-price-recovery-banner">
                  <Coins className="w-3 h-3 inline mr-1 text-sg-gold-deep" />
                  <strong>Price recovery engine</strong> — {preview.price_recovery_stats.recovered} row(s) auto-populated from Master Projects,
                  {" "}{preview.price_recovery_stats.not_found} still need a manual price. Review-Price rows are editable inline below.
                </div>
              )}

              {preview.parsing_mode === "matrix_unpivoted" && (
                <div className="bg-sg-cream-soft border border-sg-gold/40 rounded-xl p-3 text-xs text-sg-navy" data-testid="mdp-matrix-banner">
                  <Sparkles className="w-3 h-3 inline mr-1 text-sg-gold-deep" />
                  <strong>BHK price-matrix detected</strong> — auto-unpivoted into {preview.total_found} individual listings across configs: {(preview.matrix_bhk_columns || []).map((b, i) => (
                    <span key={i} className="inline-block ml-1 px-2 py-0.5 rounded-full bg-white border border-sg-line font-mono text-[10px]">{b}</span>
                  ))}
                </div>
              )}

              {preview.global_context && (preview.global_context.rera || preview.global_context.locality) && preview.global_context.inherited_row_count > 0 && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-xs text-emerald-900" data-testid="mdp-context-banner">
                  <CheckCircle2 className="w-3 h-3 inline mr-1 text-emerald-700" />
                  <strong>Global context applied</strong> to {preview.global_context.inherited_row_count} row{preview.global_context.inherited_row_count === 1 ? "" : "s"} —
                  {preview.global_context.locality && <span className="ml-1">Locality: <span className="font-semibold">{preview.global_context.locality}</span></span>}
                  {preview.global_context.locality && preview.global_context.rera && " · "}
                  {preview.global_context.rera && <span>RERA: <span className="font-mono">{preview.global_context.rera}</span></span>}
                </div>
              )}

              {preview.parsing_mode === "llm" && (
                <div className="bg-sg-cream-soft border border-sg-line rounded-xl p-3 text-xs text-sg-navy" data-testid="mdp-llm-banner">
                  <Sparkles className="w-3 h-3 inline mr-1 text-sg-gold-deep" />
                  No structured tables were detected — listings extracted with AI assistance. Please double-check each row before publishing.
                </div>
              )}

              {preview.unmapped_field_summary?.length > 0 && !hasRawHeaders && (
                <div className="bg-sg-cream-soft border border-sg-line rounded-xl p-3 text-xs text-sg-mute" data-testid="mdp-unmapped">
                  <Tags className="w-3 h-3 inline mr-1" /> Unmapped columns (ignored): {preview.unmapped_field_summary.map((u) => (
                    <span key={u.field} className="inline-block ml-1 px-2 py-0.5 rounded-full bg-white border border-sg-line font-mono">
                      {u.field} ×{u.occurrences}
                    </span>
                  ))}
                </div>
              )}

              {hasRawHeaders && (
                <div
                  className={`rounded-xl border ${overrideMetrics.unmappedCount ? "border-amber-300 bg-amber-50/40" : "border-sg-line bg-sg-cream-soft/60"}`}
                  data-testid="mdp-mapping-panel"
                >
                  <button
                    type="button"
                    onClick={() => setMappingOpen((v) => !v)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left"
                    data-testid="mdp-mapping-toggle"
                    aria-expanded={mappingOpen}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center border ${overrideMetrics.unmappedCount ? "bg-amber-100 border-amber-300 text-amber-800" : "bg-white border-sg-line text-sg-gold-deep"}`}>
                      <Columns3 className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-sg-navy flex items-center gap-2 flex-wrap">
                        Column mapping
                        <span className="text-xs font-normal text-sg-mute">{preview.raw_headers.length} column{preview.raw_headers.length === 1 ? "" : "s"}</span>
                        {overrideMetrics.unmappedCount > 0 && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300" data-testid="mdp-mapping-unmapped-badge">
                            {overrideMetrics.unmappedCount} unmapped
                          </span>
                        )}
                        {overrideMetrics.changedCount > 0 && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sg-gold/20 text-sg-gold-deep border border-sg-gold/40" data-testid="mdp-mapping-changed-badge">
                            {overrideMetrics.changedCount} overridden
                          </span>
                        )}
                        {overrideMetrics.ignoredCount > 0 && (
                          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-300" data-testid="mdp-mapping-ignored-badge">
                            {overrideMetrics.ignoredCount} ignored
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-sg-mute mt-0.5">
                        {overrideMetrics.unmappedCount
                          ? "Some columns weren't auto-detected. Map them to the right field, or leave them ignored."
                          : "All columns detected. Click to review or override any mapping."}
                      </div>
                    </div>
                    {mappingOpen ? <ChevronUp className="w-4 h-4 text-sg-mute" /> : <ChevronDown className="w-4 h-4 text-sg-mute" />}
                  </button>

                  {mappingOpen && (
                    <div className="px-4 pb-4 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_180px_minmax(0,1.2fr)] gap-2 text-[11px] text-sg-mute uppercase tracking-wide font-semibold pt-2 pb-1 border-t border-sg-line">
                        <div>File column</div>
                        <div>Maps to</div>
                        <div>Sample values</div>
                      </div>
                      {preview.raw_headers.map((h) => {
                        const current = overrides[h.name] ?? "auto";
                        const detected = h.detected_field || null;
                        const isUnmapped = !detected && (current === "auto" || current === undefined);
                        return (
                          <div
                            key={h.name}
                            className={`grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_180px_minmax(0,1.2fr)] gap-2 items-center rounded-lg border px-3 py-2 ${isUnmapped ? "bg-amber-50/50 border-amber-200" : "bg-white border-sg-line"}`}
                            data-testid={`mdp-mapping-row-${h.name}`}
                          >
                            <div className="min-w-0">
                              <div className="font-mono text-xs text-sg-navy truncate" title={h.name}>{h.name}</div>
                              {detected ? (
                                <div className="text-[10px] text-sg-mute mt-0.5">auto: <span className="font-mono">{detected}</span></div>
                              ) : (
                                <div className="text-[10px] text-amber-800 mt-0.5">not auto-detected</div>
                              )}
                            </div>
                            <div>
                              <select
                                value={current}
                                onChange={(e) => setOverrides((prev) => ({ ...prev, [h.name]: e.target.value }))}
                                className="w-full h-8 rounded-md border border-sg-line bg-white text-xs px-2 focus:outline-none focus:ring-2 focus:ring-sg-gold/40"
                                data-testid={`mdp-mapping-select-${h.name}`}
                                aria-label={`Map column ${h.name}`}
                              >
                                <option value="auto">
                                  {detected ? `Auto (${detected})` : "Auto (ignored)"}
                                </option>
                                <option value="">Ignore</option>
                                <optgroup label="Map to field">
                                  {mappableFields.map((f) => (
                                    <option key={f} value={f}>{f}</option>
                                  ))}
                                </optgroup>
                              </select>
                            </div>
                            <div className="text-xs text-sg-mute truncate" title={(h.sample_values || []).join(" · ")}>
                              {(h.sample_values || []).length
                                ? (h.sample_values || []).slice(0, 3).map((s, i) => (
                                    <span key={i} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-sg-cream-soft border border-sg-line font-mono text-[10px] truncate max-w-[140px] align-middle">
                                      {String(s).slice(0, 40)}
                                    </span>
                                  ))
                                : <em>no samples</em>}
                            </div>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between gap-2 pt-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => {
                            const reset = {};
                            preview.raw_headers.forEach((h) => { reset[h.name] = "auto"; });
                            setOverrides(reset);
                          }}
                          className="text-xs text-sg-mute hover:text-sg-navy underline underline-offset-2"
                          data-testid="mdp-mapping-reset"
                        >
                          Reset to auto
                        </button>
                        <Button
                          type="button"
                          onClick={onRemap}
                          disabled={!canRemap || remapLoading}
                          className="h-9 rounded-full bg-sg-gold-deep hover:bg-sg-gold text-white disabled:opacity-50 disabled:cursor-not-allowed px-4"
                          data-testid="mdp-mapping-apply"
                        >
                          {remapLoading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
                          Re-analyze with mapping
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between text-sm">
                <button onClick={toggleAll} className="text-sg-gold-deep border-b border-sg-gold" data-testid="mdp-toggle-all">
                  {allChecked ? "Clear selection" : "Select all committable"}
                </button>
                <div className="text-sg-mute">
                  Selected: <strong className="text-sg-navy">{Object.values(selected).filter(Boolean).length}</strong> / {committableCount} committable · {preview.items.length} total
                </div>
              </div>

              <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-1">
                {preview.items.map((it, idx) => {
                  const m = it.mapped || {};
                  const status = it.row_status || (it.is_valid ? "Valid" : "Rejected");
                  const priceEditable = status === "Review_Price" || (status === "Flagged" && (it.flags || []).includes("price_recovery_needed"));
                  const effective = effectivePriceLakhs(it, idx);
                  const committable = isCommittable(it, idx);
                  const wrapperCls =
                    status === "Valid" ? "border-sg-line bg-white"
                    : status === "Review_Price" ? "border-sg-gold/60 bg-sg-cream-soft/60"
                    : status === "Flagged" ? "border-amber-200 bg-amber-50/40"
                    : "border-red-200 bg-red-50/40 opacity-90";
                  return (
                    <div
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-xl border ${wrapperCls}`}
                      data-testid={`mdp-row-${idx}`}
                      data-status={status}
                    >
                      <input
                        type="checkbox"
                        checked={!!selected[idx]}
                        onChange={(e) => setSelected({ ...selected, [idx]: e.target.checked })}
                        disabled={!committable}
                        className="mt-1 accent-sg-gold-deep"
                        data-testid={`mdp-check-${idx}`}
                        aria-label={`Select row ${idx + 1}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <StatusPill status={status} />
                          <span className="font-semibold text-sg-navy">{m.bhk || "?"} {m.property_type || "?"}</span>
                          {m.project_name && (
                            <span className="text-sg-navy truncate max-w-[200px]" title={m.project_name}>· <em className="font-serif-italic">{m.project_name}</em></span>
                          )}
                          <span className="text-sg-mute">·</span>
                          <span className="text-sg-mute">{m.locality || <em className="text-red-700">no locality</em>}</span>
                          <span className="text-sg-mute">·</span>
                          {priceEditable ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-white border border-sg-gold/60 px-2 py-0.5" data-testid={`mdp-price-editor-${idx}`}>
                              <span className="text-xs text-sg-mute font-mono">₹</span>
                              <Input
                                type="number"
                                inputMode="decimal"
                                min={0}
                                step={0.5}
                                placeholder={m.price_lakhs ? String(m.price_lakhs) : "Lakhs"}
                                value={priceEdits[idx] ?? (m.price_lakhs || "")}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setPriceEdits((prev) => ({ ...prev, [idx]: v }));
                                  // Auto-tick when a valid number is entered
                                  if (v && Number(v) > 0) {
                                    setSelected((prev) => ({ ...prev, [idx]: true }));
                                  }
                                }}
                                className="h-6 w-20 text-xs border-0 shadow-none focus-visible:ring-0 p-0 font-mono"
                                data-testid={`mdp-price-input-${idx}`}
                              />
                              <span className="text-[10px] text-sg-mute">L</span>
                            </span>
                          ) : (
                            <span className="rupee text-sg-navy" data-testid={`mdp-price-${idx}`}>
                              {effective ? `₹${effective}L` : <em className="text-red-700">no price</em>}
                            </span>
                          )}
                          {m.carpet_area_sqft && <span className="text-xs text-sg-mute font-mono">{m.carpet_area_sqft} sqft</span>}
                          {m.external_rera_number && <span className="font-mono text-[10px] text-sg-gold-deep">RERA {m.external_rera_number}</span>}
                        </div>
                        {m.price_source === "master_projects" && m.master_project_ref && (
                          <div className="text-[11px] text-sg-gold-deep mt-1 flex items-center gap-1" data-testid={`mdp-master-hint-${idx}`}>
                            <Coins className="w-3 h-3" />
                            Auto-populated from Master Projects: <span className="font-mono">{m.master_project_ref.project_name}</span>
                            {m.master_project_ref.maharera_id && <span className="font-mono">· {m.master_project_ref.maharera_id}</span>}
                            {" — edit above if you know a different price."}
                          </div>
                        )}
                        {status === "Flagged" && (it.flags || []).includes("price_recovery_needed") && (
                          <div className="text-[11px] text-amber-800 mt-1 flex items-center gap-1" data-testid={`mdp-manual-hint-${idx}`}>
                            <HelpCircle className="w-3 h-3" />
                            Price not found in Master Projects. Please enter manually to make this row committable.
                          </div>
                        )}
                        {m.notes && <div className="text-xs text-sg-mute mt-1 line-clamp-2">{m.notes}</div>}
                        {(it.flags?.length || it.missing_mandatory?.length) ? (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(it.missing_mandatory || []).filter(mm => mm !== "price" || status === "Rejected").map((mm) => (
                              <span key={mm} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border bg-red-50 text-red-800 border-red-200">
                                <AlertTriangle className="w-2.5 h-2.5" /> missing {mm}
                              </span>
                            ))}
                            {(it.flags || []).map(flagBadge)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-3 mt-2 sm:flex-col">
            <div className="bg-sg-cream-soft border border-sg-line rounded-xl p-3 w-full" data-testid="mdp-consent-panel">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-sg-gold accent-sg-gold-deep"
                  data-testid="mdp-consent-checkbox"
                />
                <span className="text-sm text-sg-navy leading-relaxed">
                  I confirm that I own this listing or have explicit authorization from the property owner to advertise it. I verify that all data provided complies with RERA regulations and is free of unauthorized personal data.
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-2 w-full">
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full" data-testid="mdp-cancel">Cancel</Button>
              <Button
                onClick={onCommit}
                disabled={stage === "committing" || !consent}
                className="rounded-full bg-sg-navy hover:bg-sg-navy-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="mdp-commit"
              >
                {stage === "committing" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirm Sync · Import as Draft
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
